import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Customer login images - appetizing food spreads and dining
const CUSTOMER_BACKGROUND_IMAGES = [
  {
    url: 'https://static.prod-images.emergentagent.com/jobs/205ad07e-a09e-432e-b5af-7f1ac0f8bc0f/images/c07a6ccd9fe63816a857f1760e2f3d6409839bcae4f7d6bcdaf9b33c1a8b1f1b.png',
    alt: 'African-inspired lunch spread with jollof rice and grilled chicken'
  },
  {
    url: 'https://static.prod-images.emergentagent.com/jobs/205ad07e-a09e-432e-b5af-7f1ac0f8bc0f/images/cf9df4f6f684cd7c16c51021fbe0b729a128fe7ba559aed84486d40f1dc187c2.png',
    alt: 'Beautifully plated business lunch with grilled meat'
  },
  {
    url: 'https://images.unsplash.com/photo-1769638913609-aa66bedf24d9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920',
    alt: 'Buffet with pizzas, salads, and fresh fruit'
  },
  {
    url: 'https://images.unsplash.com/photo-1712565043059-cd19ff8394cb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920',
    alt: 'Close up of delicious plated food'
  },
  {
    url: 'https://images.unsplash.com/photo-1763048443535-1243379234e2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920',
    alt: 'Egusi soup with assorted meats'
  },
  {
    url: 'https://images.pexels.com/photos/17952748/pexels-photo-17952748.jpeg?auto=compress&cs=tinysrgb&w=1920',
    alt: 'West African Jollof rice with fried plantains'
  }
];

// Admin/BOH login images - professional kitchen and catering operations
const ADMIN_BACKGROUND_IMAGES = [
  {
    url: 'https://static.prod-images.emergentagent.com/jobs/205ad07e-a09e-432e-b5af-7f1ac0f8bc0f/images/186bfb883c393086495444522a6d2aa6f7fa0d91043180ad53fc1825d47c8b45.png',
    alt: 'Professional commercial kitchen with chefs preparing meals'
  },
  {
    url: 'https://static.prod-images.emergentagent.com/jobs/205ad07e-a09e-432e-b5af-7f1ac0f8bc0f/images/a7846de74d77b93569e554ed031674a72c8adaa9558634961f72131ace50e268.png',
    alt: 'Professional catering setup with chafing dishes'
  },
  {
    url: 'https://images.unsplash.com/photo-1769812344084-b45b638b1737?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920',
    alt: 'Buffet table with assorted pastries and tarts'
  },
  {
    url: 'https://images.unsplash.com/photo-1771154136172-0633db70b7dc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920',
    alt: 'Assortment of gourmet appetizers and canapés'
  },
  {
    url: 'https://images.pexels.com/photos/17878224/pexels-photo-17878224.jpeg?auto=compress&cs=tinysrgb&w=1920',
    alt: 'Buffet-style rice and vegetables in catering trays'
  },
  {
    url: 'https://images.unsplash.com/photo-1768849352377-996d0f6e4810?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920',
    alt: 'Gourmet food presentation'
  }
];

export default function RotatingBackground({ 
  interval = 15000, // 15 seconds default
  overlay = true,
  overlayColor = 'bg-secondary-900/60',
  children,
  className = '',
  variant = 'customer' // 'customer' or 'admin'
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  
  // Select image set based on variant
  const BACKGROUND_IMAGES = variant === 'admin' ? ADMIN_BACKGROUND_IMAGES : CUSTOMER_BACKGROUND_IMAGES;

  // Preload images
  useEffect(() => {
    const preloadImages = BACKGROUND_IMAGES.map(img => {
      const image = new Image();
      image.src = img.url;
      return image;
    });
    
    // Mark as loaded when first image is ready
    preloadImages[0].onload = () => setIsLoaded(true);
  }, [variant]);

  // Rotate images
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % BACKGROUND_IMAGES.length);
    }, interval);

    return () => clearInterval(timer);
  }, [interval, BACKGROUND_IMAGES.length]);

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {/* Background Images */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 1.05 }}
          transition={{ 
            duration: 1.5, 
            ease: [0.4, 0, 0.2, 1]
          }}
          className="absolute inset-0"
        >
          <img
            src={BACKGROUND_IMAGES[currentIndex].url}
            alt={BACKGROUND_IMAGES[currentIndex].alt}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </motion.div>
      </AnimatePresence>

      {/* Overlay */}
      {overlay && (
        <div className={`absolute inset-0 ${overlayColor}`} />
      )}

      {/* Gradient overlay for better text readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/20" />

      {/* Progress Indicators */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10">
        {BACKGROUND_IMAGES.map((_, index) => (
          <motion.div
            key={index}
            className={`h-1.5 rounded-full transition-all duration-500 ${
              index === currentIndex 
                ? 'w-8 bg-white' 
                : 'w-1.5 bg-white/40 hover:bg-white/60'
            }`}
            animate={{
              scale: index === currentIndex ? 1 : 0.8
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 h-full">
        {children}
      </div>
    </div>
  );
}

export { CUSTOMER_BACKGROUND_IMAGES, ADMIN_BACKGROUND_IMAGES };
